import React, { useState } from 'react';
import { track } from '@vercel/analytics';
import './Page.css';
import usePageTitle from '../hooks/usePageTitle';

function Contact() {
  usePageTitle('Contact Us');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        track('Contact Form Submitted', { subject: formData.subject });
        setStatus({ type: 'success', message: 'Thank you for your message! We will respond shortly.' });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        const data = await response.json();
        setStatus({ type: 'error', message: data.error || 'Something went wrong. Please try again.' });
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'Unable to send message. Please email us directly at muncylodge299@gmail.com' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-hero contact-hero" style={{ backgroundImage: 'url(/LodgeSignClean.jpg)', backgroundPosition: 'center center' }}>
        <h1>Contact Us</h1>
        <p>Get in Touch with Muncy Lodge No. 299</p>
      </div>

      <div className="page-content">
        <div className="contact-grid">
          <div className="contact-info-section">
            <h2 className="section-heading">Lodge Information</h2>
            <div className="contact-details">
              <div className="contact-item">
                <h3>Address</h3>
                <p>
                  Muncy Lodge No. 299 F & A.M.<br />
                  28 Brummer Pond Rd<br />
                  Muncy, PA 17756
                </p>
              </div>

              <div className="contact-item">
                <h3>Secretary</h3>
                <p>
                  Email: <a href="mailto:Secy0299@pagrandlodge.org">Secy0299@pagrandlodge.org</a><br />
                  Phone: <a href="tel:+15705463280">(570) 546-3280</a>
                </p>
              </div>

              <div className="contact-item">
                <h3>Meeting Times</h3>
                <p>
                  Stated Meetings: First Thursday of each month<br />
                  Time: 7:30 PM<br />
                  Dinner: 6:30 PM
                </p>
              </div>

              <div className="contact-item">
                <h3>Connect With Us</h3>
                <div className="social-links-contact">
                  <a 
                    href="https://www.facebook.com/muncylodge299" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="social-icon"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="contact-form-section">
            <h2 className="section-heading">Send Us a Message</h2>
            {status.message && (
              <div className={`form-status ${status.type}`} style={{
                padding: '1rem',
                marginBottom: '1rem',
                borderRadius: '8px',
                background: status.type === 'success' ? '#e8f5e9' : '#fce4ec',
                color: status.type === 'success' ? '#2e7d32' : '#c62828',
                border: `1px solid ${status.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
              }}>
                {status.message}
              </div>
            )}
            <form onSubmit={handleSubmit} className="contact-form">
              {/* Honeypot field for spam protection — hidden from users */}
              <input
                type="text"
                name="_honeypot"
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a subject</option>
                  <option value="membership">Membership Inquiry</option>
                  <option value="event">Event Information</option>
                  <option value="rental">Hall Rental</option>
                  <option value="general">General Question</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  required
                ></textarea>
              </div>

              <button type="submit" className="cta-button primary" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>

        <section className="content-section">
          <h2>Find Us</h2>
          <div className="map-container-full">
            <iframe
              title="Muncy Lodge Location"
              src="https://www.google.com/maps?q=28+Brummer+Pond+Rd,+Muncy,+PA+17756&output=embed"
              width="100%"
              height="400"
              style={{ border: 0, borderRadius: '8px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Contact;
